import { useQueryClient } from '@tanstack/react-query'
import { MutationOptions, useMutateResource } from '../resource'
import {
  CreateQuizAttemptDocument,
  CreateQuizAttemptMutation,
  CreateQuizAttemptMutationVariables,
  QuizAttemptFragment,
  UpdateQuizAttemptDocument,
  UpdateQuizAttemptMutation,
  UpdateQuizAttemptMutationVariables,
} from '../types'

type CreateQuizAttemptOptions = MutationOptions<
  CreateQuizAttemptMutation,
  CreateQuizAttemptMutationVariables
>

type UpdateQuizAttemptOptions = MutationOptions<
  UpdateQuizAttemptMutation,
  UpdateQuizAttemptMutationVariables
>

export type QuizAttemptType = ReturnType<typeof deriveQuizAttempt>

/**
 * @private
 * The cache key name utilized for the `QuizAttempt` Resource.
 */
const KEY_NAME = 'QuizAttempt'

/**
 * @private
 * Derives data not contained in the API.
 */
function deriveQuizAttempt(quizAttempt?: QuizAttemptFragment) {
  if (quizAttempt == null) return quizAttempt
  return {
    ...quizAttempt,
  }
}

/**
 * @public
 * A hook mutation to create a `QuizAttempt` resource.
 *
 * @example
 * const createQuizAttempt = useCreateQuizAttempt()
 * const changeQuizAttempt = async () => {
 *   const input = { quiz: { title: 'Learn about a moose.', ... } }
 *   createQuizAttempt.mutate({input})
 * }
 */
export function useCreateQuizAttempt(options?: CreateQuizAttemptOptions) {
  const queryClient = useQueryClient()
  const result = useMutateResource<
    CreateQuizAttemptMutation,
    CreateQuizAttemptMutationVariables
  >(KEY_NAME + 'Create', CreateQuizAttemptDocument, {
    ...options,
    onSuccess: () => {
      queryClient.invalidateQueries(['Quiz'])
    },
  })
  const data =
    result.data != null
      ? { getQuizAttempt: deriveQuizAttempt(result.data.createQuizAttempt) }
      : undefined
  return { ...result, data }
}

/**
 * @public
 * A mutation to update a `QuizAttempt` resource.
 * The resource will be created if one doesn't exist.
 *
 * @example
 * const updateQuizAttempt = useUpdateQuizAttempt()
 * const changeQuizAttempt = async () => {
 *   const input = { quiz: { title: 'Learn about meese.', ... } }
 *   updateQuizAttempt.mutate({input})
 * }
 */
export function useUpdateQuizAttempt(options?: UpdateQuizAttemptOptions) {
  const queryClient = useQueryClient()
  const result = useMutateResource<
    UpdateQuizAttemptMutation,
    UpdateQuizAttemptMutationVariables
  >(KEY_NAME + 'Update', UpdateQuizAttemptDocument, {
    ...options,
    onSuccess: (data) => {
      queryClient.invalidateQueries(['Quiz'])
      queryClient.invalidateQueries(['ClientTraining'])
      if (data?.updateQuizAttempt?.status !== 'STARTED') {
        queryClient.invalidateQueries(['Training'])
      }
    },
  })
  const data =
    result.data != null
      ? { getQuizAttempt: deriveQuizAttempt(result.data.updateQuizAttempt) }
      : undefined
  return { ...result, data }
}
